// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon, CheckBox as CheckBoxIcon } from '@mui/icons-material';
import { Autocomplete, Checkbox, Chip, Stack, TextField, Divider } from '@mui/material';
import { BasicInputPlaceholder, TooltipInfo } from '../../../../ui';

const DEFAULT_LABELS = {
  label: 'Selection',
  noValues: 'None',
};

const SELECT_ALL_KEY = '__SELECT_ALL__';

export const MultiSelectWithSelectAll = ({
  id,
  labels: tmpLabels,
  tooltipText,
  selectedKeys,
  disabled = false,
  options,
  onChange,
  isDirty,
  error,
}) => {
  const { t } = useTranslation();
  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => a.key.localeCompare(b.key));
  }, [options]);
  const labels = { ...DEFAULT_LABELS, ...tmpLabels };

  const allKeys = useMemo(() => sortedOptions.map((o) => o.key), [sortedOptions]);

  const isAllSelected = selectedKeys.length === allKeys.length;

  const optionsWithSelectAll = useMemo(() => {
    if (!sortedOptions.length) return [];
    return [
      { key: SELECT_ALL_KEY, value: t('genericcomponent.multiSelect.selectAll', 'Select All') },
      ...sortedOptions,
    ];
  }, [sortedOptions, t]);

  const displayKeys = useMemo(
    () => (isAllSelected ? [...allKeys, SELECT_ALL_KEY] : selectedKeys),
    [isAllSelected, selectedKeys, allKeys]
  );

  const getLabelFromEnumKey = useCallback(
    (key) => optionsWithSelectAll.find((opt) => opt.key === key)?.value || key,
    [optionsWithSelectAll]
  );

  const handleChange = (_, values) => {
    const hasSelectAll = values.includes(SELECT_ALL_KEY);
    const filtered = values.filter((v) => v !== SELECT_ALL_KEY);

    if (hasSelectAll && !isAllSelected) {
      onChange(allKeys);
    } else if (!hasSelectAll && isAllSelected) {
      onChange([]);
    } else {
      onChange(filtered);
    }
  };

  const renderOption = useCallback(
    (props, optionKey, { selected }) => {
      if (optionKey === SELECT_ALL_KEY) {
        return (
          <>
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {getLabelFromEnumKey(optionKey)}
            </li>
            <Divider
              sx={{
                margin: '6px auto',
                width: '92%',
                backgroundColor: 'grey.300',
                height: '1.5px',
              }}
            />
          </>
        );
      }
      return (
        <li {...props}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {getLabelFromEnumKey(optionKey)}
        </li>
      );
    },
    [getLabelFromEnumKey]
  );
  if (disabled) {
    const selectedValues =
      selectedKeys.length > 0 ? selectedKeys.map((key) => getLabelFromEnumKey(key)).join(', ') : labels.noValues;

    return (
      <BasicInputPlaceholder
        id={`multi-input-${id}`}
        label={labels.label ?? id}
        tooltipText={tooltipText}
        value={selectedValues}
      />
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        ...(isDirty === true
          ? { pl: 2, borderLeft: (theme) => `3px solid ${theme.palette.info.main}` }
          : isDirty === false
            ? { pl: 2.5 }
            : {}),
      }}
      data-cy={`multi-input-${id}`}
    >
      <Autocomplete
        multiple
        id={id}
        disableCloseOnSelect
        options={optionsWithSelectAll.map((o) => o.key)}
        value={displayKeys}
        onChange={handleChange}
        renderOption={(props, key, state) => renderOption(props, key, state)}
        renderTags={(value, getTagProps) =>
          value
            .filter((v) => v !== SELECT_ALL_KEY)
            .map((key, index) => <Chip key={key} label={getLabelFromEnumKey(key)} {...getTagProps({ index })} />)
        }
        getOptionLabel={(key) => getLabelFromEnumKey(key)}
        renderInput={(params) => <TextField {...params} label={labels.label ?? id} fullWidth error={!!error} />}
        ListboxProps={{ 'data-cy': 'multi-input-listbox' }}
        sx={{ width: 500 }}
      />
      <TooltipInfo title={tooltipText} variant="small" />
    </Stack>
  );
};

MultiSelectWithSelectAll.propTypes = {
  id: PropTypes.string.isRequired,
  labels: PropTypes.object,
  tooltipText: PropTypes.string,
  selectedKeys: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  isDirty: PropTypes.bool,
  error: PropTypes.object,
};
